(() => {
  'use strict';

  const STORAGE_KEY = 'lieblingsplatz_click_collect_v1';
  const STATUS_LABELS = Object.freeze({
    available: ['Verfügbar', 'Dieser Artikel kann in der gewählten Filiale vorgemerkt werden.'],
    transit_only: ['Aktuell nicht zur Abholung verfügbar', 'Dieser Artikel ist unterwegs in eine Filiale und kann momentan noch nicht reserviert werden.'],
    unavailable: ['Nicht verfügbar', 'Diese Variante ist derzeit in keiner Filiale verfügbar.'],
    unknown: ['Derzeit nicht prüfbar', 'Die Verfügbarkeit konnte nicht geprüft werden. Bitte versuche es später erneut.'],
  });
  const STORE_LABELS = Object.freeze({
    4: 'Lieblingsplatz Siegburg', 5: 'Lieblingsplatz Langenfeld',
    6: 'Comma Plus Gummersbach', 7: 'Lieblingsplatz St. Augustin',
    9: 'Lieblingsplatz ACC Koblenz', 11: 'Lieblingsplatz Troisdorf',
    12: 'Lieblingsplatz Dortmund', 17: 'Lieblingsplatz Gummersbach',
    19: 'Lieblingsplatz Mülheim', 20: 'Lieblingsplatz Köln-Kalk',
    21: 'LP Accessoires St. Augustin', 22: 'Lieblingsplatz Siegburg Concept',
    31: 'Lieblingsplatz Hennef', 32: 'Lieblingsplatz Brühl',
    33: 'Lieblingspatz Bad Godesberg', 35: 'Lieblingspatz Hennef 2',
  });

  function findVariant(variants, selectedOptions) {
    return variants.find((variant) => (
      variant.options.length === selectedOptions.length
      && variant.options.every((value, index) => value === selectedOptions[index])
    )) || null;
  }

  function addReservationItem(current, store, item) {
    const basket = current && current.version === 1
      ? structuredClone(current)
      : { version: 1, store: null, items: [] };

    if (basket.store && Number(basket.store.storeNo) !== Number(store.storeNo)) {
      return { basket, error: `Dieser Korb ist bereits der Filiale ${basket.store.storeLabel} zugeordnet.` };
    }

    basket.store = { storeNo: Number(store.storeNo), storeLabel: String(store.storeLabel) };
    const existing = basket.items.find((entry) => entry.variantId === item.variantId);
    if (existing) existing.quantity += 1;
    else basket.items.push({ ...item, quantity: 1 });
    return { basket, error: '' };
  }

  function emptyBasket() {
    return { version: 1, store: null, items: [] };
  }

  class ClickCollectProduct {
    constructor(root) {
      this.root = root;
      this.apiBase = root.dataset.apiBase.replace(/\/$/, '');
      this.product = JSON.parse(root.querySelector('[data-click-collect-product-json]').textContent);
      this.optionGroups = [...root.querySelectorAll('[data-product-option]')].map((group) => ({
        index: Number(group.dataset.optionIndex),
        buttons: [...group.querySelectorAll('[data-option-value]')],
      }));
      this.storeSelect = root.querySelector('[data-store-select]');
      this.reserveButton = root.querySelector('[data-reserve-button]');
      this.basketOpen = root.querySelector('[data-basket-open]');
      this.basketSection = root.querySelector('[data-reservation-basket]');
      this.availabilityPanel = root.querySelector('[data-availability-panel]');
      this.variantEanDisplay = root.querySelector('[data-variant-ean]');
      this.supplierColorDisplay = root.querySelector('[data-supplier-color-display]');
      this.supplierColorValue = root.querySelector('[data-supplier-color-value]');
      this.supplierMaterialDisplay = root.querySelector('[data-supplier-material-display]');
      this.supplierMaterialValue = root.querySelector('[data-supplier-material-value]');
      this.availabilityTitle = root.querySelector('[data-availability-title]');
      this.availabilityMessage = root.querySelector('[data-availability-message]');
      this.basketItems = root.querySelector('[data-basket-items]');
      this.basketEmpty = root.querySelector('[data-basket-empty]');
      this.basketStore = root.querySelector('[data-basket-store]');
      this.basketTotal = root.querySelector('[data-basket-total]');
      this.basketError = root.querySelector('[data-basket-error]');
      this.basketClear = root.querySelector('[data-basket-clear]');
      this.reservationForm = root.querySelector('[data-reservation-form]');
      this.reservationSubmit = root.querySelector('[data-reservation-submit]');
      this.reservationStatus = root.querySelector('[data-reservation-status]');
      this.reservationFormTitle = root.querySelector('[data-reservation-form-title]');
      this.selectedOptions = this.product.options.map(() => '');
      // Shopify exposes product options as { name, values } objects in the
      // Click-&-Collect JSON. Reading the object itself made the color index
      // -1, so clicks were acknowledged but could never filter media.
      this.colorOptionIndex = this.product.options.findIndex((option) => (
        /farbe|color|colour/i.test(typeof option === 'string' ? option : option.name)
      ));
      this.colorValues = new Set(this.colorOptionIndex >= 0
        ? this.product.options[this.colorOptionIndex].values
        : []);
      this.selectedVariant = null;
      this.availabilityStatus = 'idle';
      this.storeWasUserSelected = false;
      this.requestSequence = 0;
      this.stores = new Map();
      this.basket = this.readBasket();
      this.idempotencyKey = '';
      this.submitted = false;
      this.bind();
      this.bindExternalColorSelection();
      this.selectSingletonOptions();
      this.renderBasket();
      this.selectionChanged();
    }

    bind() {
      this.optionGroups.forEach((group) => group.buttons.forEach((button) => button.addEventListener('click', () => {
        if (button.disabled) return;
        this.selectedOptions[group.index] = button.dataset.optionValue;
        this.setPressed(group.buttons, button);
        this.updateOptionChoices();
        this.selectionChanged();
      })));
      this.storeSelect.addEventListener('change', () => this.storeSelectionChanged(true));
      this.reserveButton.addEventListener('click', () => this.addToBasket());
      this.basketClear.addEventListener('click', () => {
        this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
      });
      this.basketOpen.addEventListener('click', () => this.openBasket());
      this.basketItems.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-variant]');
        if (!button) return;
        this.basket.items = this.basket.items.filter((item) => item.variantId !== button.dataset.removeVariant);
        if (!this.basket.items.length) this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
      });
      this.reservationForm.addEventListener('submit', (event) => this.submitReservation(event));
      this.revealExternalColorOptions();
    }

    bindExternalColorSelection() {
      this.externalColorClick = (event) => {
        if (this.root.contains(event.target)) return;
        const candidates = event.composedPath()
          .filter((item) => item instanceof Element)
          .slice(0, 8)
          .flatMap((element) => [
            element.dataset?.optionValue,
            element.dataset?.value,
            element.value,
            element.getAttribute('aria-label'),
            element.textContent,
          ])
          .map((value) => String(value || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        const value = [...this.colorValues].find((color) => candidates.some((candidate) => (
          candidate === color || candidate.includes(color)
        )));
        if (value) this.selectColor(value);
      };
      document.addEventListener('click', this.externalColorClick, true);
      this.externalColorObserver = new MutationObserver(() => this.revealExternalColorOptions());
      this.externalColorObserver.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'disabled']});
      this.revealExternalColorOptions();
    }

    revealExternalColorOptions() {
      // Camouflage owns visibility of its native controls. Do not remove its
      // unavailable classes or disabled state; the Click-&-Collect controls
      // below apply the same availability decision from storeAvailability.
    }

    selectColor(color) {
      if (this.colorOptionIndex < 0 || !this.colorValues.has(color)) return;
      this.selectedOptions[this.colorOptionIndex] = color;
      const group = this.optionGroups.find((item) => item.index === this.colorOptionIndex);
      if (group) {
        const button = group.buttons.find((item) => item.dataset.optionValue === color);
        if (button) this.setPressed(group.buttons, button);
      }
      this.updateOptionChoices();
      this.selectionChanged();
    }

    selectSingletonOptions() {
      this.optionGroups.forEach((group) => {
        if (group.buttons.length !== 1) return;
        const button = group.buttons[0];
        this.selectedOptions[group.index] = button.dataset.optionValue;
        this.setPressed(group.buttons, button);
      });
      this.updateOptionChoices();
    }

    updateOptionChoices() {
      this.optionGroups.forEach((group) => group.buttons.forEach((button) => {
        const candidate = [...this.selectedOptions];
        candidate[group.index] = button.dataset.optionValue;
        const matches = this.product.variants.filter((variant) => variant.options.every(
          (value, index) => !candidate[index] || candidate[index] === value,
        ));
        // Camouflage hides unavailable colors as well as unavailable sizes.
        // A color with no reservable variant must therefore not be selectable.
        button.disabled = !matches.length || !matches.some((variant) => this.isReservable(variant));
      }));
    }

    isReservable(variant) {
      const availability = this.product.storeAvailability?.variants;
      if (!availability) return true;
      return Array.isArray(availability[String(variant.barcode)])
        && availability[String(variant.barcode)].length > 0;
    }

    selectionChanged() {
      if (this.submitted) return;
      const previousStore = this.storeSelect.value;
      this.selectedVariant = findVariant(this.product.variants, this.selectedOptions);
      this.updateSupplierColor();
      this.filterMediaByColor();
      this.reserveButton.disabled = true;
      this.availabilityStatus = 'idle';
      this.reserveButton.textContent = 'Zur Reservierung hinzufügen';
      this.requestSequence += 1;
      this.stores.clear();
      this.storeSelect.disabled = true;
      const barcode = String(this.selectedVariant?.barcode || '').trim();
      if (this.variantEanDisplay) {
        this.variantEanDisplay.textContent = barcode ? `EAN: ${barcode}` : '';
        this.variantEanDisplay.hidden = !barcode;
      }
      if (this.selectedVariant) {
        this.root.querySelector('[data-product-price]').textContent = this.money(
          Number(this.selectedVariant.price), this.product.currency,
        );
      }
      if (!this.selectedVariant) {
        this.storeSelect.replaceChildren(new Option('Zuerst Farbe und Größe wählen', ''));
        this.setIdle();
        return;
      }
      this.storeSelect.replaceChildren(new Option('Verfügbare Filialen werden geladen …', ''));
      this.loadAvailableStores(this.selectedVariant, previousStore, this.requestSequence);
    }

    filterMediaByColor() {
      const color = this.colorOptionIndex >= 0
        ? String(this.selectedOptions[this.colorOptionIndex] || '').trim()
        : '';
      const supplierColors = new Set(
        this.product.variants
          .filter((variant) => (
            !color || variant.options[this.colorOptionIndex] === color
          ))
          .map((variant) => String(variant.supplierColor || '').trim())
          .filter(Boolean),
      );
      const productSearchColors = new Set(
        this.product.variants
          .map((variant) => String(variant.options[this.colorOptionIndex] || '').trim())
          .filter(Boolean),
      );
      const isMulticolor = /^(mehrfarbig|multicolor|multi[- ]?colou?r)$/i.test(color);
      const isSingleSearchColorProduct = productSearchColors.size === 1;
      this.root.querySelectorAll('[data-media-color]').forEach((media) => {
        const mediaColor = media.dataset.mediaColor.trim();
        const matches = !color || mediaColor === color || supplierColors.has(mediaColor)
          || (isMulticolor && isSingleSearchColorProduct);
        media.hidden = !matches;
        media.classList.toggle('is-hidden-by-color', !matches);
        media.style.display = matches ? '' : 'none';
      });
    }

    updateSupplierColor() {
      if (!this.supplierColorDisplay || !this.supplierColorValue || this.colorOptionIndex < 0) return;
      const color = String(this.selectedOptions[this.colorOptionIndex] || '').trim();
      const variant = this.product.variants.find((item) => item.options[this.colorOptionIndex] === color);
      const supplierColor = String(variant?.supplierColor || '').trim();
      const supplierMaterial = String(variant?.supplierMaterial || '').trim();
      this.supplierColorValue.textContent = supplierColor;
      this.supplierColorDisplay.hidden = !supplierColor;
      if (this.supplierMaterialDisplay && this.supplierMaterialValue) {
        this.supplierMaterialValue.textContent = supplierMaterial;
        this.supplierMaterialDisplay.hidden = !supplierMaterial;
      }
    }

    async loadAvailableStores(variant, previousStore, sequence) {
      this.availabilityPanel.dataset.status = 'loading';
      this.availabilityTitle.textContent = 'Verfügbarkeit wird geprüft …';
      this.availabilityMessage.textContent = 'Wir prüfen diese Variante in allen Filialen.';
      const localAvailability = this.product.storeAvailability;
      if (localAvailability && localAvailability.variants) {
        const numbers = localAvailability.variants[String(variant.barcode)] || [];
        const variantState = localAvailability.variant_states?.[String(variant.barcode)];
        if (!numbers.length && variantState === 'transit_only') {
          this.storeSelect.replaceChildren(new Option('Derzeit keine Abholfiliale verfügbar', ''));
          this.storeSelect.disabled = true;
          this.setAvailability('transit_only');
          return;
        }
        this.renderAvailableStores(numbers.map((storeNo) => ({
          store_no: storeNo,
          store_name: STORE_LABELS[Number(storeNo)] || `Filiale ${storeNo}`,
          status: 'available',
        })), previousStore, sequence);
        return;
      }
      const adminVariantId = `gid://shopify/ProductVariant/${variant.id}`;
      const query = new URLSearchParams({ variant_id: adminVariantId });
      try {
        const response = await fetch(`${this.apiBase}/available-stores?${query}`, {
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('availability_unavailable');
        const result = await response.json();
        if (sequence !== this.requestSequence || result.variant_id !== adminVariantId) return;
        if (result.status === 'unknown' || !Array.isArray(result.stores)) {
          this.storeSelect.replaceChildren(new Option('Filialen derzeit nicht prüfbar', ''));
          this.setAvailability('unknown');
          return;
        }
        const stores = result.stores;
        this.renderAvailableStores(stores, previousStore, sequence);
      } catch (_error) {
        if (sequence !== this.requestSequence) return;
        this.storeSelect.replaceChildren(new Option('Filialen derzeit nicht prüfbar', ''));
        this.storeSelect.disabled = true;
        this.setAvailability('unknown');
      }
    }

    renderAvailableStores(rawStores, previousStore, sequence) {
      if (sequence !== this.requestSequence) return;
      const stores = rawStores.filter((store) => (
        Number.isInteger(Number(store.store_no))
        && typeof store.store_name === 'string'
        && store.status === 'available'
      ));
        this.storeSelect.replaceChildren(new Option('Filiale auswählen', ''));
        stores.forEach((store) => {
          const storeNo = String(Number(store.store_no));
          const label = store.store_name;
          this.stores.set(storeNo, { storeNo: Number(store.store_no), storeLabel: label });
          this.storeSelect.add(new Option(label, storeNo));
        });
        if (!stores.length) {
          this.storeSelect.replaceChildren(new Option('Keine Filiale verfügbar', ''));
          this.setAvailability('unavailable');
          return;
        }
        this.storeSelect.disabled = false;
        if (stores.length === 1) {
          this.storeSelect.value = String(Number(stores[0].store_no));
          this.storeWasUserSelected = false;
        }
        else if (this.storeWasUserSelected && this.stores.has(previousStore)) this.storeSelect.value = previousStore;
        else this.storeWasUserSelected = false;
        this.storeSelectionChanged(false);
    }

    storeSelectionChanged(userInitiated = false) {
      if (userInitiated) this.storeWasUserSelected = true;
      if (!this.selectedVariant || !this.stores.has(this.storeSelect.value)) {
        this.availabilityStatus = 'idle';
        this.availabilityPanel.dataset.status = 'idle';
        this.availabilityTitle.textContent = 'Filiale auswählen';
        this.availabilityMessage.textContent = `Diese Variante ist in ${this.stores.size} Filialen verfügbar.`;
        this.reserveButton.disabled = true;
        return;
      }
      this.setAvailability('available');
    }

    setAvailability(status) {
      const copy = STATUS_LABELS[status] || STATUS_LABELS.unknown;
      this.availabilityStatus = status;
      this.availabilityPanel.dataset.status = status;
      this.availabilityTitle.textContent = copy[0];
      this.availabilityMessage.textContent = copy[1];
      this.reserveButton.disabled = status !== 'available';
    }

    setIdle() {
      this.availabilityPanel.dataset.status = 'idle';
      this.availabilityTitle.textContent = 'Auswahl vervollständigen';
      this.availabilityMessage.textContent = 'Wähle zuerst alle Variantenoptionen.';
    }

    addToBasket() {
      if (this.availabilityStatus !== 'available' || !this.selectedVariant) return;
      const store = this.stores.get(this.storeSelect.value);
      if (!store) return;
      // Jede Anfrage wird einzeln abgeschlossen; ein neuer Artikel ersetzt daher
      // einen eventuell noch offenen, nicht abgeschlossenen Korb.
      const result = addReservationItem(emptyBasket(), store, {
        productId: this.product.productId,
        variantId: `gid://shopify/ProductVariant/${this.selectedVariant.id}`,
        title: this.product.title,
        image: this.selectedVariant.image || this.product.image,
        selectedOptions: this.product.options.map((option, index) => ({
          name: option.name,
          value: this.selectedVariant.options[index],
        })),
        priceCents: Number(this.selectedVariant.price),
        currency: this.product.currency,
      });
      this.basketError.textContent = result.error;
      if (result.error) return;
      this.basket = result.basket;
      this.writeBasket();
      this.renderBasket();
      this.openBasket();
      this.reserveButton.textContent = 'Im Reservierungskorb';
    }

    readBasket() {
      try {
        const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!value || value.version !== 1 || !Array.isArray(value.items)) return emptyBasket();
        // Alte Versionen konnten mehrere Positionen sammeln. Eine offene
        // Reservierung darf künftig immer nur genau eine Position enthalten.
        const items = value.items.slice(-1);
        return items.length ? { ...value, items } : emptyBasket();
      } catch (_error) {
        return emptyBasket();
      }
    }

    writeBasket() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.basket));
      } catch (_error) {
        this.basketError.textContent = 'Der lokale Reservierungskorb konnte nicht gespeichert werden.';
      }
    }

    renderBasket() {
      const hasItems = this.basket.items.length > 0;
      this.basketOpen.hidden = !hasItems;
      this.basketItems.replaceChildren();
      this.basketEmpty.hidden = hasItems;
      this.basketStore.hidden = !hasItems;
      this.basketTotal.hidden = !hasItems;
      this.basketClear.hidden = !hasItems;
      this.reservationForm.hidden = !hasItems;
      this.basketStore.textContent = hasItems ? `Filiale: ${this.basket.store.storeLabel}` : '';
      let total = 0;
      this.basket.items.forEach((item) => {
        total += item.priceCents * item.quantity;
        const row = document.createElement('li');
        row.className = 'cc-basket__item';
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        const details = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = item.title;
        const choices = document.createElement('span');
        const selectedOptions = Array.isArray(item.selectedOptions)
          ? item.selectedOptions.map((option) => option.value)
          : [item.color, item.size].filter(Boolean);
        choices.textContent = `${selectedOptions.join(' · ')} · Menge ${item.quantity}`;
        const price = document.createElement('span');
        price.textContent = this.money(item.priceCents * item.quantity, item.currency);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.removeVariant = item.variantId;
        remove.textContent = 'Entfernen';
        details.append(title, choices, price, remove);
        row.append(image, details);
        this.basketItems.append(row);
      });
      if (hasItems) this.basketTotal.querySelector('strong').textContent = this.money(total, this.basket.items[0].currency);
    }

    openBasket() {
      if (!this.basket.items.length) return;
      this.basketSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.basketSection.focus({ preventScroll: true });
    }

    async submitReservation(event) {
      event.preventDefault();
      if (!this.basket.items.length || !this.basket.store) return;
      if (!this.reservationForm.reportValidity()) {
        this.reservationStatus.textContent = 'Bitte fülle Name, E-Mail und Telefon vollständig aus. Beispiel für Telefon: 0170 1234567';
        return;
      }
      this.reservationSubmit.disabled = true;
      this.reservationStatus.textContent = 'Anfrage wird sicher geprüft …';
      if (!this.idempotencyKey) this.idempotencyKey = crypto.randomUUID();
      const form = new FormData(this.reservationForm);
      const payload = {
        store_no: Number(this.basket.store.storeNo),
        customer: {
          name: String(form.get('name') || ''),
          email: String(form.get('email') || ''),
          phone: String(form.get('phone') || ''),
          note: String(form.get('note') || ''),
        },
        items: this.basket.items.map((item) => ({
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
        })),
      };
      try {
        const response = await fetch(`${this.apiBase}/reservations`, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': this.idempotencyKey,
            'X-Click-Collect-Request': '1',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.detail || 'reservation_failed');
        this.reservationStatus.textContent = `Anfrage ${result.reservation_no} ist eingegangen. Die Filiale prüft sie jetzt. Bestätigte Ware wird drei Werktage zurückgelegt.`;
        this.basket = emptyBasket();
        this.writeBasket();
        this.renderBasket();
        this.submitted = true;
        this.basketEmpty.hidden = true;
        this.reserveButton.disabled = true;
        this.reserveButton.textContent = 'Reservierung angefragt';
        this.reservationForm.hidden = false;
        this.reservationForm.classList.add('is-complete');
        this.reservationFormTitle.textContent = 'Anfrage gesendet';
      } catch (_error) {
        this.reservationStatus.textContent = 'Die Anfrage konnte nicht gesendet werden. Bitte prüfe deine Angaben oder versuche es später erneut.';
        this.reservationSubmit.disabled = false;
      }
    }

    money(cents, currency) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency || 'EUR' }).format(cents / 100);
    }

    setPressed(buttons, active) {
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button === active)));
    }
  }

  globalThis.ClickCollectProductPoc = Object.freeze({ findVariant, addReservationItem, emptyBasket, STATUS_LABELS });
  if (typeof document !== 'undefined') {
    document.querySelectorAll('[data-click-collect-product]').forEach((root) => new ClickCollectProduct(root));
  }
})();
